import Vue from 'vue'
import { jsPDF } from 'jspdf'
import 'jspdf-autotable'
import utils from '@/mixins/utils.js'
import store from '@/store/store'
import { vue } from '@/main.js'
import { dataFields } from './utils.js'

/**
 * Retorna os cadastros de uma equipe
 *
 * @param {int} cod_ibge - IBGE do município
 * @param {int} nu_ine - INE da Equipe
 *
 * @returns {Array}
 */
export async function getCadastrosPorEquipe(cod_ibge, nu_ine) {
  try {
    const { data } = await Vue.prototype.$api.get(
      `/app/desenvolvimento-infantil/${cod_ibge}/equipe/${nu_ine}`
    )
    return data
  } catch (err) {
    console.log(err)
  }
}

/**
 * Retorna os cadastros por microárea
 *
 * @param {*} cod_ibge
 * @param {*} nu_ine
 * @param {*} micro_area
 *
 * @returns {Array}
 */
export async function getCadastrosPorMicroArea(cod_ibge, nu_ine, micro_area) {
  try {
    const { data } = await Vue.prototype.$api.get(
      `/app/desenvolvimento-infantil/${cod_ibge}/equipe/${nu_ine}/microarea/${micro_area}`
    )
    return data
  } catch (err) {
    console.log(err)
  }
}

/**
 * Gera o relatório em PDF.
 *
 * @param {Array} cadastros
 * @param {Object} selectEquipe
 * @param {String} microArea
 * @param {Object} selectedFilterBoasPraticas
 */
export function generatePdf(
  cadastros,
  selectEquipe,
  microArea,
  selectedFilterBoasPraticas
) {
  let title_pdf = 'Desenvolvimento infantil'
  let titlePdfSave = utils.methods.titlePdf('Desenvolvimento infantil')

  let description_pdf = 'Cuidado no desenvolvimento infantil'
  let headers_pdf = ['NOME', 'NASCIMENTO', 'MÃE', 'M.ÁREA', 'PENDÊNCIAS']
  var data_pdf = []

  for (let i = 0; i < cadastros.length; i++) {
    var obj = {}
    obj.no_cidadao = cadastros[i].no_cidadao
    obj.dt_nascimento =
      utils.methods.formatDate(cadastros[i].dt_nascimento) +
      `\n(${Math.floor(cadastros[i].idade_dias / 30)} ${
        Math.floor(cadastros[i].idade_dias / 30) > 1 ? 'meses' : 'mês'
      })`
    obj.no_mae = cadastros[i].no_mae
    obj.micro_area = cadastros[i].micro_area
    obj.pendencias = dataFields.Pendências.callback(cadastros[i])
    obj.rowColor = cadastros[i].rowColor
    data_pdf.push(obj)
  }

  let doc = new jsPDF({
    orientation: 'l',
    unit: 'pt',
    format: 'a4',
    ...utils.methods.encryptionPdf(),
  })
  // cria a tabela
  let table = utils.methods.createTablePdf(data_pdf)

  let brasaoParaUsar = store.state.infoMunicipio.brasao

  utils.methods
    .fileToBase64(brasaoParaUsar)
    .then((base64data) => {
      // Header
      let verticalPosition = utils.methods.headerPdf(
        doc,
        base64data,
        title_pdf,
        description_pdf,
        200,
        'l'
      )

      verticalPosition = utils.methods.bodyPdf(
        doc,
        [
          {
            label: 'Equipe',
            value: selectEquipe.no_equipe,
          },
          {
            label: 'CNES',
            value: selectEquipe.nu_cnes,
          },
          {
            label: 'INE',
            value: selectEquipe.nu_ine,
          },
          {
            label: 'Microárea',
            value: microArea,
          },
          {
            label: 'Boa prática',
            value: selectedFilterBoasPraticas?.label ?? '',
          },
        ],
        verticalPosition,
        'l'
      )

      doc.autoTable({
        head: [headers_pdf],
        body: table,
        headStyles: {
          fillColor: '#ccc',
          fontSize: 12,
          textColor: '#000',
          halign: 'center',
          cellWidth: 'wrap',
        },
        bodyStyles: {
          halign: 'center',
          fontSize: 12,
        },
        theme: 'striped',
        startY: verticalPosition,
        showHead: 'firstPage',
        willDrawCell: function (data) {
          let color = data.row.raw[5]

          // Header sempre preto
          if (data.row.section === 'head') {
            doc.setTextColor(0, 0, 0)
            return
          }
          if (color == 'green') {
            doc.setTextColor(2, 99, 57)
            return
          }
          if (color == 'red') {
            doc.setTextColor(194, 10, 16)
            return
          }
        },
      })

      utils.methods.footerPdf(doc, doc.autoTable.previous.finalY, 250, 600, 'l')

      doc.save(titlePdfSave + '.pdf')

      vue.$toast.success('PDF GERADO COM SUCESSO!', 'Sucesso:', {
        position: 'topRight',
      })
    })
    .catch((error) => {
      console.error(error)
      vue.$toast.error('ERRO AO GERAR PDF!', 'Erro:', {
        position: 'topRight',
      })
    })
}

function montarResultado(dados, tipoResultado = 'quadrimestral') {
  if (tipoResultado === 'mensal') {
    return {
      numerador: dados.numerador_all,
      denominador: dados.denominador_all,
      resultado: dados.resultado_all,
      classificacao: dados.classificacao_all,
    }
  }

  return {
    numerador: dados.numerador,
    denominador: dados.denominador,
    resultado: dados.resultado,
    classificacao: dados.classificacao,
  }
}

/**
 * Percentual do município
 *
 * @param {Array} dadosDaTabelaResultado
 * @param {Object} selectedFilterBoasPraticas
 * @param {String} tipoResultado
 * @returns {Object}
 */
export function percentualMunicipio(
  dadosDaTabelaResultado,
  selectedFilterBoasPraticas,
  tipoResultado = 'quadrimestral'
) {
  let numerador = 0
  let denominador = 0
  let resultado = 0
  let classificacao = ''

  const hoje = new Date()
  const mesAtual = hoje.getMonth() + 1
  const anoAtual = String(hoje.getFullYear())
  const mesAnoAtual = `${mesAtual}/${anoAtual}`

  const dadosDoMunicipio = dadosDaTabelaResultado.find(
    (item) =>
      item.nivel == 1 &&
      item.ibge === store.state.cod_ibge &&
      item.indicador == 51 &&
      item.mes_ano === mesAnoAtual
  )

  if (dadosDoMunicipio === undefined) {
    return {
      numerador: 0,
      denominador: 0,
      resultado: 0,
      classificacao: '',
    }
  }

  let dados = JSON.parse(dadosDoMunicipio.dados)

  if (
    selectedFilterBoasPraticas == null ||
    selectedFilterBoasPraticas.value == null
  ) {
    return montarResultado(dados, tipoResultado)
  } else {
    numerador = dados[`num_${selectedFilterBoasPraticas.key_percentual}`]
    denominador = dados.denominador
    resultado = dados[`res_${selectedFilterBoasPraticas.key_percentual}`]
    classificacao = '-'
  }

  return {
    numerador: numerador,
    denominador: denominador,
    resultado: resultado,
    classificacao: classificacao,
  }
}

/**
 * Percentual da equipe
 *
 * @param {Array} dadosDaTabelaResultado
 * @param {String} nuIne
 * @param {Object} selectedFilterBoasPraticas
 * @param {String} tipoResultado
 * @returns {Object}
 */
export function percentualEquipe(
  dadosDaTabelaResultado,
  nuIne,
  selectedFilterBoasPraticas,
  tipoResultado = 'quadrimestral'
) {
  let numerador = 0
  let denominador = 0
  let resultado = 0
  let classificacao = ''

  const hoje = new Date()
  const mesAtual = hoje.getMonth() + 1
  const anoAtual = String(hoje.getFullYear())
  const mesAnoAtual = `${mesAtual}/${anoAtual}`

  let dadosDaEquipe = dadosDaTabelaResultado.find(
    (item) =>
      item.nivel == 2 &&
      item.ine === nuIne &&
      item.indicador == 51 &&
      item.mes_ano === mesAnoAtual
  )

  if (dadosDaEquipe === undefined) {
    return {
      numerador: 0,
      denominador: 0,
      resultado: 0,
      classificacao: '',
    }
  }

  let dados = JSON.parse(dadosDaEquipe.dados)

  if (
    selectedFilterBoasPraticas == null ||
    selectedFilterBoasPraticas.value == null
  ) {
    return montarResultado(dados, tipoResultado)
  } else {
    numerador = dados[`num_${selectedFilterBoasPraticas.key_percentual}`]
    denominador = dados.denominador
    resultado = dados[`res_${selectedFilterBoasPraticas.key_percentual}`]
    classificacao = '-'
  }

  return {
    numerador: numerador,
    denominador: denominador,
    resultado: resultado,
    classificacao: classificacao,
  }
}

/**
 * Percentual da equipe
 *
 * @param {Array} dadosDaTabelaResultado
 * @param {String} nu_ine
 * @param {String} micro_area
 * @param {Object} selectedFilterBoasPraticas
 * @param {String} tipoResultado
 * @returns {Object}
 */
export function percentualMicroArea(
  dadosDaTabelaResultado,
  nu_ine,
  micro_area,
  selectedFilterBoasPraticas,
  tipoResultado = 'quadrimestral'
) {
  let numerador = 0
  let denominador = 0
  let resultado = 0
  let classificacao = ''

  const hoje = new Date()
  const mesAtual = hoje.getMonth() + 1
  const anoAtual = String(hoje.getFullYear())
  const mesAnoAtual = `${mesAtual}/${anoAtual}`

  let dadosDaMicroArea = dadosDaTabelaResultado.find(
    (item) =>
      item.nivel == 3 &&
      item.ine == nu_ine &&
      item.micro_area === micro_area &&
      item.indicador == 51 &&
      item.mes_ano === mesAnoAtual
  )

  if (dadosDaMicroArea === undefined) {
    return {
      numerador: 0,
      denominador: 0,
      resultado: 0,
      classificacao: classificacao,
    }
  }

  let dados = JSON.parse(dadosDaMicroArea.dados)

  if (
    selectedFilterBoasPraticas == null ||
    selectedFilterBoasPraticas.value == null
  ) {
    return montarResultado(dados, tipoResultado)
  } else {
    numerador = dados[`num_${selectedFilterBoasPraticas.key_percentual}`]
    denominador = dados.denominador
    resultado = dados[`res_${selectedFilterBoasPraticas.key_percentual}`]
    classificacao = '-'
  }

  return {
    numerador: numerador,
    denominador: denominador,
    resultado: resultado,
    classificacao: classificacao,
  }
}

/**
 * Formata os cadastros com as doses de vacinas
 *
 * @param {Array} cadastros
 * @returns {Array}
 */
export function formatarCadastrosComDoses(cadastros) {
  return cadastros.map((item, index) => {
    const vacinasPendentes = JSON.parse(item.vacinas_pendentes)
    const vacinas = {
      penta: JSON.parse(item.penta) ?? [],
      vip: JSON.parse(item.vip) ?? [],
      vpc10: JSON.parse(item.vpc10) ?? [],
      vpc20: JSON.parse(item.vpc20 || '[]') ?? [],
      scr: JSON.parse(item.scr) ?? [],
    }

    const vacinaConfig = [
      {
        nome: 'coresPentaValente',
        vacina: 'penta',
        id_mn: '42',
        doses: [
          { label: '1ª DOSE', id_ds: '1', idade: '2 meses' },
          { label: '2ª DOSE', id_ds: '2', idade: '4 meses' },
          { label: '3ª DOSE', id_ds: '3', idade: '6 meses' },
        ],
      },
      {
        nome: 'coresVip',
        vacina: 'vip',
        id_mn: '22',
        doses: [
          { label: '1ª DOSE', id_ds: '1', idade: '2 meses' },
          { label: '2ª DOSE', id_ds: '2', idade: '4 meses' },
          { label: '3ª DOSE', id_ds: '3', idade: '6 meses' },
          {
            label: '1º REFORÇO',
            id_ds: '6',
            id_ds_aplicada: ['6', '38'],
            idade: '15 meses',
          },
          { label: '2º REFORÇO', id_ds: '7', idade: '4 anos' },
        ],
      },
      {
        nome: 'coresPneumococica',
        vacina: 'vpc10',
        id_mn: '26',
        doses: [
          { label: '1º DOSE', id_ds: '1', idade: '2 meses' },
          { label: '2ª DOSE', id_ds: '2', idade: '4 meses' },
          { label: 'REFORÇO', id_ds: '38', idade: '12 meses' },
          { label: 'ÚNICA', id_ds: '9', idade: '12 meses a 4 anos' },
        ],
      },
      {
        nome: 'coresPneumococica20',
        vacina: 'vpc20',
        id_mn: '107',
        doses: [
          { label: '1ª DOSE', id_ds: '1', idade: '2 meses' },
          { label: '2ª DOSE', id_ds: '2', idade: '4 meses' },
          { label: 'ÚNICA', id_ds: '9', idade: '12 meses' },
          { label: 'REFORÇO', id_ds: '38', idade: '12 meses a 4 anos' },
        ],
      },
      {
        nome: 'coresSrc',
        vacina: 'scr',
        id_mn: '24',
        doses: [
          { label: '1ª DOSE', id_ds: '1', idade: '12 meses' },
          { label: '2ª DOSE', id_ds: '2', idade: '15 meses' },
          { label: 'DOSE', id_ds: '8', idade: '' },
        ],
      },
    ]

    const resultadoVacinas = vacinaConfig.reduce((acc, config) => {
      acc[config.nome] = processarDoses(
        vacinas[config.vacina],
        config.id_mn,
        config.doses,
        vacinasPendentes
      )
      return acc
    }, {})

    let fvdt = []

    try {
      fvdt = JSON.parse(item.fvdt) ?? []
    } catch (error) {
      fvdt = []
    }

    const visita1 = fvdt.find((el) => {
      return el.id_visita == 1 && el.dias_nasc_ate_visita <= 30
    })

    const visita2 = fvdt.find((el) => {
      return (
        el.id_visita > 1 &&
        el.dias_nasc_ate_visita > 30 &&
        el.dias_nasc_ate_visita <= 182
      )
    })

    const nao_tem_primeira_visita = visita1 === undefined
    const nao_tem_segunda_visita = visita2 === undefined

    const fora_prazo_visita1 =
      nao_tem_primeira_visita && Number(item.idade_meses) > 0

    const fora_prazo_visita2 =
      nao_tem_segunda_visita && Number(item.idade_dias) > 182

    return {
      id: index,
      ...item,
      ...resultadoVacinas,
      nao_tem_primeira_visita: visita1 === undefined,
      nao_tem_segunda_visita: visita2 === undefined,
      fora_prazo_visita1: fora_prazo_visita1,
      fora_prazo_visita2: fora_prazo_visita2,
    }
  })
}

/**
 * Monta os cards de dose de uma vacina (cor, data e ids).
 * Aceita `id_ds_aplicada` na config para priorizar IDs na busca da dose aplicada
 * (ex.: VIP 1º reforço: ['6', '38']).
 *
 * @param {Array} vacina Lista de doses aplicadas da vacina
 * @param {String|Number} id_mn Identificador do imunobiológico
 * @param {Array} dosesConfig Configuração das doses (label, id_ds, idade, id_ds_aplicada?)
 * @param {Array} vacinasPendentes Lista de vacinas pendentes
 * @returns {Array} Lista de doses processadas
 */
function processarDoses(vacina, id_mn, dosesConfig, vacinasPendentes) {
  return dosesConfig.map(({ label, id_ds, idade, id_ds_aplicada }) => {
    const dose = encontrarDose(
      vacina,
      id_mn,
      id_ds,
      vacinasPendentes,
      id_ds_aplicada
    )

    return {
      dose: label,
      cor: dose.cor,
      tag: dose.tag || '',
      id_ds: dose.id_ds || '',
      id_mn: dose.id_mn || '',
      dt_plc: dose.tipo === 'aplicada' ? dose.dt_plc : idade,
      dt_plc_br: dose.tipo === 'aplicada' ? dose.dt_plc_br : idade,
      orig: dose.orig || '',
      lt: dose.lt || '',
    }
  })
}

/**
 * Localiza a dose aplicada ou pendente de uma vacina.
 * Ordem: aplicada (por prioridade de id_ds_aplicada ou id_ds) → pendente (id_ds) → branco.
 * Usado no VIP 1º reforço para buscar aplicada em '6' e, se não houver, em '38',
 * mantendo a pendência apenas em '6'.
 *
 * @param {Array} doses Lista de doses aplicadas
 * @param {String|Number} id_mn Identificador do imunobiológico
 * @param {String|Number} id_ds Identificador da dose (pendência e fallback da aplicada)
 * @param {Array} vacinasPendentes Lista de vacinas pendentes
 * @param {Array} [id_ds_aplicada] IDs alternativos para dose aplicada (ordem de prioridade)
 * @returns {Object} Dados da dose encontrada ou padrão (cor green/red/white)
 */
function encontrarDose(
  doses,
  id_mn,
  id_ds,
  vacinasPendentes,
  id_ds_aplicada = null
) {
  const idsAplicada =
    Array.isArray(id_ds_aplicada) && id_ds_aplicada.length
      ? id_ds_aplicada
      : [id_ds]

  for (const idAplicada of idsAplicada) {
    const aplicada = doses.find(
      (el) => el.id_ds == idAplicada && el.id_mn == id_mn
    )
    if (aplicada) {
      return {
        tipo: 'aplicada',
        cor: 'green',
        ...aplicada,
        dt_plc_br: aplicada.dt_plc?.split('-').reverse().join('/') || '',
      }
    }
  }

  const pendente = vacinasPendentes?.find(
    (el) => el.id_ds == id_ds && el.id_mn == id_mn && el.na == '0'
  )
  if (pendente) {
    return {
      tipo: 'pendente',
      cor: 'red',
      ...pendente,
      dt_plc_br: '',
      orig: pendente.orig || '',
      lt: pendente.lt || '',
    }
  }

  return {
    tipo: 'pendente',
    id_ds: id_ds,
    id_mn: id_mn,
    cor: 'white',
    orig: '',
    lt: '',
  }
}
